import { useCallback } from 'react';
import { useDatabase } from '@/context/DatabaseContext';
import { getProjectIdByPath as getSpaceIdByPath, getProjectIdByName, getListIdByName, getFoldersByProjectId } from '@/db/projects';
import { useAppStore } from '@/store/appStore';
import type { Task } from '@/types';

/**
* Hook per gestire le azioni CRUD sui task.
* Converte automaticamente il path in containerId quando necessario.
* 
* @returns Oggetto con funzioni per creare, aggiornare e eliminare task
* @returns handleCreateTask - Crea un nuovo task e lo seleziona automaticamente
* @returns handleUpdateTask - Aggiorna un task esistente
* @returns handleDeleteTask - Elimina un task e deseleziona se era selezionato
*/
export function useTaskActions() {
  const { createTask, updateTask, deleteTask } = useDatabase();
  const filters = useAppStore((state) => state.filters);
  const selectedTaskId = useAppStore((state) => state.selectedTaskId);
  const setSelectedTaskId = useAppStore((state) => state.setSelectedTaskId);
  const closeCreateTask = useAppStore((state) => state.closeCreateTask);

  const handleCreateTask = useCallback(async (taskData: Partial<Task>) => {
  // If spaceId is already set directly (e.g., from CreateTaskModal with ID-based selection), use it
  let containerId: string | undefined = taskData.spaceId || undefined;

  if (!containerId) {
    // Get path from filters (now ID-based)
    const path = filters.path;

    // The last segment of an ID-based path is the most specific container ID
    if (path) {
      const pathParts = path.split('/');
      containerId = pathParts[pathParts.length - 1];
    }

    // Fallback: construct path from spaceName/folderPath/listName if provided
    if (!containerId && (taskData.spaceName || taskData.folderPath || taskData.listName)) {
      const parts: string[] = [];
      if (taskData.spaceName) parts.push(taskData.spaceName);
      if (taskData.folderPath && taskData.folderPath.length > 0) parts.push(taskData.folderPath[0]);
      if (taskData.listName) parts.push(taskData.listName);
      const fallbackPath = parts.join('/');
      containerId = await getSpaceIdByPath(fallbackPath) || undefined;
    }

    // Legacy fallback: try name-based lookup
    if (!containerId && taskData.spaceName && taskData.listName) {
      const projectId = await getProjectIdByName(taskData.spaceName);
      if (projectId) {
        let listId = await getListIdByName(projectId, taskData.listName);
        if (!listId) {
          const folders = await getFoldersByProjectId(projectId);
          for (const folder of folders) {
            listId = await getListIdByName(folder.id, taskData.listName);
            if (listId) break;
          }
        }
        if (listId) containerId = listId;
      }
    }
  }

  if (!containerId) {
      throw new Error(`Cannot find container for task creation`);
  }

  const newTask = await createTask({
      ...taskData,
      spaceId: containerId as Task['spaceId'],
  });
  setSelectedTaskId(newTask.id);
  closeCreateTask();
  }, [createTask, filters, setSelectedTaskId, closeCreateTask]);

  const handleUpdateTask = useCallback((taskId: string, updates: Partial<Task>) => {
  updateTask(taskId, updates);
  }, [updateTask]);

  const handleDeleteTask = useCallback(async (taskId: string) => {
  await deleteTask(taskId);
  if (selectedTaskId === taskId) {
      setSelectedTaskId(null);
  }
  }, [deleteTask, selectedTaskId, setSelectedTaskId]);

  return {
  handleCreateTask,
  handleUpdateTask,
  handleDeleteTask,
  };
}

